import { ActionResponse } from './app'
import { CONTENT_TYPE } from '~/shared/types/content'
import { PaginationPageSettings } from '~/shared/types/paginatedDataset'

export { CONTENT_TYPE }

export const enum CONTENT_SOURCE {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

export const enum API_TYPE {
  ASYNCAPI = 'asyncapi',
  OPENAPI = 'openapi',
  GRAPHQL = 'graphql',
  A2A = 'a2a-1.0.1',
  MCP = 'mcp',
  GRPC = 'grpc',
  INVALID = 'invalid',
}

export const DEFINITION_MARKDOWN_API_TYPES = [
  API_TYPE.GRAPHQL,
  API_TYPE.A2A,
  API_TYPE.MCP,
  API_TYPE.GRPC,
] as const

export type DefinitionMarkdownApiType = typeof DEFINITION_MARKDOWN_API_TYPES[number]

export type TableOfContentsItem = ApiContent | DocumentContent

export interface ApiContent {
  id: string,
  slug: string,
  title: string,
  content: ApiUrlContent
  swaggerhubApi?: {
    private: boolean
  }
}

export interface DocumentContent {
  id: string,
  slug: string,
  title: string,
  content: DocumentItem,
  children: TableOfContentsItem[]
}
export interface TableOfContents {
  page?: PaginationPageSettings,
  items: Array<TableOfContentsItem>
}

export interface ApiUrlContent {
  type: CONTENT_TYPE.API_URL,
  url?: string,
  documentId?: never,
  apiSpec: string,
}

export interface DocumentItem {
  type: CONTENT_TYPE.MARKDOWN | CONTENT_TYPE.HTML,
  documentId: string,
  url?: never
}

export interface Document {
  id?: string,
  type: CONTENT_TYPE,
  source: CONTENT_SOURCE,
  content: string,
  cleanedContent?: string,
  createdAt?: string,
  updatedAt?: string,
  createdBy?: string,
  updatedBy?: string,
  version?: number,
}

export interface PublishedSections {
  page?: PaginationPageSettings,
  items: PublishedSectionsItem[],
}

export interface PublishedSectionsItem {
  title: string,
  slug: string,
  tableOfContents: PublishedTableOfContents[],
}

export type PublishedTableOfContents = ApiTocItem | DocumentTocItem

export interface DocumentTocItem {
  tableOfContentsId: string,
  sourceTemplateTocId?: string,
  type: CONTENT_TYPE.MARKDOWN | CONTENT_TYPE.HTML,
  apiType?: unknown,
  title: string,
  path: string,
  children: PublishedTableOfContents[],
}

export interface ApiTocItem {
  tableOfContentsId?: string,
  sourceTemplateTocId?: string,
  type: CONTENT_TYPE.API_URL | CONTENT_TYPE.GRAPHQL | CONTENT_TYPE.MCP | CONTENT_TYPE.A2A | CONTENT_TYPE.GRPC,
  apiSpec: string,
  title: string,
  url?: string,
  path?: string,
  operations?: Operation[],
  schemas?: Schema[]
  asyncApiMessages?: AsyncApiMessage[]
  asyncApiServers?: AsyncApiServer[]
  graphqlComponents?: GraphQlComponent[]
  mcpComponents?: MCPComponent[]
  a2aComponents?: A2AComponent[]
  grpcComponents?: GRPCComponent[]
}

export interface DefinitionMarkdownComponent {
  title: string,
  items: DefinitionMarkdownComponentItem[],
}

export interface DefinitionMarkdownComponentItem {
  title: string,
  id: string,
}

export type GraphQlComponent = DefinitionMarkdownComponent
export type GraphQlComponentItem = DefinitionMarkdownComponentItem
export type MCPComponent = DefinitionMarkdownComponent
export type MCPComponentItem = DefinitionMarkdownComponentItem
export type A2AComponent = DefinitionMarkdownComponent
export type A2AComponentItem = DefinitionMarkdownComponentItem
export type GRPCComponent = DefinitionMarkdownComponent
export type GRPCComponentItem = DefinitionMarkdownComponentItem

export enum OPERATION_METHOD {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
  PATCH = 'patch',
  SUB = 'sub',
  PUB = 'pub',
  REC = 'rec',
  SEND = 'send',
  QUERY = 'query',
  OPTIONS = 'options',
}

export interface Operation {
  method: OPERATION_METHOD,
  path: string,
  tag?: string,
  summary: string,
  operationId: string,
  deprecated?: boolean,
}

export interface Schema {
  name: string,
  title?: string,
}

export interface AsyncApiMessage {
  messageId: string,
}

export interface AsyncApiServer {
  serverId: string,
}

export interface PublishedStatus {
  updatedAt?: string
}

export interface PublishedDifference {
  added: UnpublishedChange[],
  modified: UnpublishedChange[],
  removed: UnpublishedChange[],
  missing: UnpublishedChange[],
  comments?: UnresolvedComment[],
  reordered: boolean
}

export interface PublishedDifferenceItem {
  tableOfContentsId: string,
  path: string,
  title: string,
}

export interface UnpublishedChange extends PublishedDifferenceItem {
  modifications?: ContentDiffTypeEntry[],
  type: CONTENT_TYPE,
  deleted?: boolean,
}

export enum ContentDiffType {
  API_CHANGED = 'API_CHANGED',
  TITLE_CHANGED = 'TITLE_CHANGED',
  SLUG_CHANGED = 'SLUG_CHANGED',
  DOCUMENT_CHANGED = 'DOCUMENT_CHANGED',
}

export interface ContentDiffTypeEntry {
  type: ContentDiffType
}

export interface UnresolvedComment extends PublishedDifferenceItem {
  type: UNRESOLVED_COMMENT_TYPE,
}

export enum UNRESOLVED_COMMENT_TYPE {
  COMMENT = 'comment',
  SUGGESTION = 'suggestion',
  MENTION = 'mention',
}

export interface ContentDiff {
  added: boolean,
  modified: boolean,
  modifiedTypes?: ContentDiffTypeEntry[]
  removed: boolean,
  missing: boolean,
}

export interface ContentAnnotations {
  comments: boolean,
  suggestions: boolean,
  mentions: boolean,
}

export interface ApiContentDiff extends ApiContent, ContentDiff { }
export interface DocumentContentDiff extends DocumentContent, ContentDiff, ContentAnnotations {
  children: TableOfContentsItemDiff[]
}

export type TableOfContentsItemDiff = ApiContentDiff | DocumentContentDiff

export interface NotificationData {
  title: string,
  id: string,
  shubUrl?: string,
  type?: CONTENT_TYPE,
  deleted?: boolean,
}

export interface NotificationCenterData {
  added?: NotificationData[]
  modified?: NotificationData[]
  inaccessibleAPI?: NotificationData[]
  apiChanged?: NotificationData[]
  removed?: NotificationData[]
  reordered?: boolean
}

export enum EDIT_CONTENT_TYPE {
  PRODUCT = 'product',
  TEMPLATE = 'template',
}

export interface DuplicatePageActionData extends ActionResponse {
  targetPageId?: string;
}

export interface EditContentLoader<T> {
  tableOfContentsItems: T[];
  publishedDifference: PublishedDifference;
}